// The graphic resource library, simulated. RES-03 asks for "illustrations, pictogrammes,
// icônes, éléments décoratifs, formes géométriques, silhouettes, lettrages", classified by
// category, shown as thumbnails, added to the personalisation, then movable, resizable,
// rotatable and recolourable, and imported "exclusivement au format SVG". The real content is
// bought by the client from Magnific and handed to Wanadev as packs: nothing here is one of
// those. The geometric shapes are drawn from their own path, the pictograms borrow the icon
// set the app already ships, so the step has something to show.
//
// Prototype data, CDC domain RES (RES-03, an option). Tracked in PROTO-NOTES.md.

type TResourceCategory = 'decoratifs' | 'formes' | 'pictogrammes';

interface IGraphicResource {
    category: TResourceCategory;
    /** An icon of the app's own set, for the categories drawn from it. */
    icon?: string;
    id: string;
    label: string;
    /** An SVG path in a 24 x 24 box, for the shapes drawn here. */
    path?: string;
}

const GRAPHIC_RESOURCE_CATEGORIES: { id: TResourceCategory; label: string }[] = [
    { id: 'formes', label: 'Formes' },
    { id: 'pictogrammes', label: 'Pictogrammes' },
    { id: 'decoratifs', label: 'Décoratifs' },
];

const GOBELETCUP_GRAPHIC_RESOURCES: IGraphicResource[] = [
    // Formes géométriques, drawn here
    { category: 'formes', id: 'forme-carre', label: 'Carré', path: 'M4 4h16v16H4z' },
    { category: 'formes', id: 'forme-cercle', label: 'Cercle', path: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z' },
    { category: 'formes', id: 'forme-triangle', label: 'Triangle', path: 'M12 3l9 16H3z' },
    { category: 'formes', id: 'forme-losange', label: 'Losange', path: 'M12 2l10 10-10 10L2 12z' },
    { category: 'formes', id: 'forme-hexagone', label: 'Hexagone', path: 'M7 3h10l5 9-5 9H7l-5-9z' },
    {
        category: 'formes',
        id: 'forme-pastille',
        label: 'Pastille',
        path: 'M8 4h8a4 4 0 0 1 4 4v8a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4V8a4 4 0 0 1 4-4z',
    },
    {
        category: 'formes',
        id: 'forme-etoile',
        label: 'Étoile',
        path: 'M12 2l2.9 6.3 6.9.8-5.1 4.7 1.4 6.8L12 17.3 5.9 20.6l1.4-6.8L2.2 9.1l6.9-.8z',
    },
    {
        category: 'formes',
        id: 'forme-coeur',
        label: 'Cœur',
        path: 'M12 21S2.6 15.2 2.6 9.6C2.6 6.5 5 4.4 7.8 4.4c1.8 0 3.3 1 4.2 2.4.9-1.4 2.4-2.4 4.2-2.4 2.8 0 5.2 2.1 5.2 5.2 0 5.6-9.4 11.4-9.4 11.4z',
    },
    { category: 'formes', id: 'forme-fleche', label: 'Flèche', path: 'M3 10h10V5l8 7-8 7v-5H3z' },
    { category: 'formes', id: 'forme-eclair', label: 'Éclair', path: 'M13 2L4 14h6l-1 8 9-12h-6z' },
    { category: 'formes', id: 'forme-goutte', label: 'Goutte', path: 'M12 2s7 7.6 7 12a7 7 0 0 1-14 0c0-4.4 7-12 7-12z' },
    { category: 'formes', id: 'forme-croix', label: 'Croix', path: 'M10 3h4v7h7v4h-7v7h-4v-7H3v-4h7z' },

    // Pictogrammes, from the app's own set
    { category: 'pictogrammes', icon: 'StarIcon', id: 'picto-etoile', label: 'Étoile' },
    { category: 'pictogrammes', icon: 'HeartIcon', id: 'picto-coeur', label: 'Cœur' },
    { category: 'pictogrammes', icon: 'SparklesIcon', id: 'picto-etincelles', label: 'Étincelles' },
    { category: 'pictogrammes', icon: 'SunIcon', id: 'picto-soleil', label: 'Soleil' },
    { category: 'pictogrammes', icon: 'MoonIcon', id: 'picto-lune', label: 'Lune' },
    { category: 'pictogrammes', icon: 'BoltIcon', id: 'picto-eclair', label: 'Éclair' },
    { category: 'pictogrammes', icon: 'CloudIcon', id: 'picto-nuage', label: 'Nuage' },
    { category: 'pictogrammes', icon: 'FireIcon', id: 'picto-flamme', label: 'Flamme' },
    { category: 'pictogrammes', icon: 'GiftIcon', id: 'picto-cadeau', label: 'Cadeau' },
    { category: 'pictogrammes', icon: 'MusicalNoteIcon', id: 'picto-note', label: 'Note' },
    { category: 'pictogrammes', icon: 'TrophyIcon', id: 'picto-trophee', label: 'Trophée' },
    { category: 'pictogrammes', icon: 'CakeIcon', id: 'picto-gateau', label: 'Gâteau' },

    // Éléments décoratifs
    { category: 'decoratifs', icon: 'FaceSmileIcon', id: 'deco-sourire', label: 'Sourire' },
    { category: 'decoratifs', icon: 'HandThumbUpIcon', id: 'deco-pouce', label: 'Pouce' },
    { category: 'decoratifs', icon: 'BellIcon', id: 'deco-cloche', label: 'Cloche' },
    { category: 'decoratifs', icon: 'TagIcon', id: 'deco-etiquette', label: 'Étiquette' },
    { category: 'decoratifs', icon: 'PaperAirplaneIcon', id: 'deco-avion', label: 'Avion' },
    { category: 'decoratifs', icon: 'PuzzlePieceIcon', id: 'deco-puzzle', label: 'Puzzle' },
    { category: 'decoratifs', icon: 'RocketLaunchIcon', id: 'deco-fusee', label: 'Fusée' },
    { category: 'decoratifs', icon: 'MapPinIcon', id: 'deco-repere', label: 'Repère' },
];

export { GOBELETCUP_GRAPHIC_RESOURCES, GRAPHIC_RESOURCE_CATEGORIES };

export type { IGraphicResource, TResourceCategory };
